import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Check, CircleAlert, Info, LucideAngularModule, X } from 'lucide-angular';
import { NgClass, NgStyle } from '@angular/common';
import { ToastService } from '@app/core/services/toast.service';
import { ToastType } from '@app/core/types/toast-type';

/**
 * Toast notification component responsible for displaying temporary alerts/messages.
 *
 * This component is created and managed by `ToastService`, and not used directly in templates.
 * It supports automatic dismissal with a pause-on-hover feature and animations.
 */
@Component({
  selector: 'app-toast',
  imports: [
    TranslatePipe,
    LucideAngularModule,
    NgStyle,
    NgClass
  ],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.css'
})
export class ToastComponent {
  /**
   * Reference to the container element for animation control.
   */
  @ViewChild('toastContainer') public toastContainer?: ElementRef;

  /**
   * Pauses the auto-dismiss timer when the user hovers over the toast.
   */
  @HostListener('mouseenter')
  public mouseEnter(): void {
    this.pauseTimer();
    this.hovered = true;
  }

  /**
   * Resumes the auto-dismiss timer when the user stops hovering.
   */
  @HostListener('mouseleave')
  public mouseLeave(): void {
    this.resumeTimer();
    this.hovered = false;
  }

  /** ----------------------------- **/
  /** Inputs provided by ToastService **/
  /** ----------------------------- **/

  /**
   * Unique ID for this toast instance (used for closing).
   */
  public id: number = 0;

  /**
   * The title shown in the toast (optional).
   */
  public title?: string;

  /**
   * The message body shown in the toast (optional).
   */
  public message?: string;

  /**
   * The toast type, which affects icon and styling.
   */
  public type: ToastType = 'success';

  /**
   * Duration in milliseconds before the toast auto-dismisses.
   */
  public duration: number = 3000;

  /** ----------------------------- **/
  /** Internal State & Icon Mapping **/
  /** ----------------------------- **/

  /**
   * Indicates whether the user is currently hovering the toast.
   */
  public hovered: boolean = false;

  /**
   * Internal reference to the auto-dismiss timer.
   */
  private timer?: ReturnType<typeof setTimeout>;

  /**
   * Lucide icon for success toasts.
   */
  protected readonly Check = Check;

  /**
   * Lucide icon for close button.
   */
  protected readonly X = X;

  /**
   * Lucide icon for warning/error toasts.
   */
  protected readonly CircleAlert = CircleAlert;

  /**
   * Lucide icon for info toasts.
   */
  protected readonly Info = Info;

  /**
   * Constructs the toast component and starts the auto-dismiss timer.
   *
   * @param toastService - The service used to manage toast lifecycles.
   */
  constructor(
    private readonly toastService: ToastService,
  ) {
    this.resumeTimer();
  }

  /**
   * Closes the toast manually or after expiration, with animation.
   */
  public close(): void {
    this.toastContainer?.nativeElement.classList.remove('animate-fadeinright');
    this.toastContainer?.nativeElement.classList.add('opacity-0', 'translate-y-4', 'scale-95', 'transition-all', 'duration-300');

    setTimeout(() => {
      clearTimeout(this.timer);
      this.toastService.close(this.id);
    }, 300);
  }

  /**
   * Pauses the dismissal timer, preventing the toast from closing while hovered.
   */
  public pauseTimer(): void {
    clearTimeout(this.timer);
  }

  /**
   * Resumes the dismissal timer, triggering automatic closure after the defined duration.
   */
  public resumeTimer(): void {
    this.timer = setTimeout(() => {
      this.close();
    }, this.duration);
  }
}
